import React, { useState, useCallback } from "react";
import { ChevronDown } from "react-feather";
import { EvidenceCard } from "../evidence-card/evidence-card";
import type { EvidenceCardConfig } from "../criterion-question/criterion-question";
import type { ChecklistOption } from "../criterion-checklist/criterion-checklist";
import "./criterion-detailed-checklist.scss";

export type { EvidenceCardConfig, ChecklistOption };

export interface CriterionDetailedChecklistProps {
  stepNumber?: number | string;
  title: string;
  badge?: React.ReactNode;

  checklistName: string;
  checklistOptions: ChecklistOption[];
  checkedValues?: string[];
  onCheckChange?: (
    value: string,
    checked: boolean,
    allChecked: string[]
  ) => void;
  checklistDisabled?: boolean;
  checkedColor?: string;

  textareaName?: string;
  textareaValue?: string;
  onTextChange?: (
    value: string,
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => void;
  textareaPlaceholder?: string;
  textareaDisabled?: boolean;
  textareaRows?: number;
  textareaMaxLength?: number;
  textareaLabel?: string;
  /** Hide the textarea entirely (with reveal animation). */
  shouldHideTextBox?: boolean;
  /** When true, the textarea must contain non-empty content; otherwise a red error border is shown. */
  isTextRequired?: boolean;
  /** Optional error message shown below the textarea when validation fails. */
  textRequiredErrorMessage?: string;

  evidenceCards?: EvidenceCardConfig[];
  /** @deprecated Use evidenceCards instead */
  evidenceTitle?: string;
  /** @deprecated Use evidenceCards instead */
  evidenceStatements?: string[];
  /** @deprecated Use evidenceCards instead */
  evidenceCardBackGroundColor?: string;

  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;

  /** Optional content rendered between the header row and the checklist options. */
  headerSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  id?: string;
}

const CriterionDetailedChecklist = ({
  stepNumber,
  title,
  badge,
  checklistName,
  checklistOptions,
  checkedValues = [],
  onCheckChange,
  checklistDisabled = false,
  checkedColor,
  textareaName,
  textareaValue,
  onTextChange,
  textareaPlaceholder = "Enter your notes here…",
  textareaDisabled = false,
  textareaRows = 4,
  textareaMaxLength,
  textareaLabel,
  shouldHideTextBox = false,
  isTextRequired = false,
  textRequiredErrorMessage = "This field is required.",
  evidenceCards,
  evidenceTitle,
  evidenceStatements,
  evidenceCardBackGroundColor,
  collapsible = false,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  headerSlot,
  children,
  className,
  id,
}: CriterionDetailedChecklistProps) => {
  const isControlled = expandedProp !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expandedProp : internalExpanded;

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  }, [isExpanded, isControlled, onExpandedChange]);

  const titleId = id ? `${id}-title` : undefined;
  const textareaId = id ? `${id}-textarea` : undefined;
  const textareaLabelId = id ? `${id}-textarea-label` : undefined;

  const resolvedEvidenceCards: EvidenceCardConfig[] =
    evidenceCards != null && evidenceCards.length > 0
      ? evidenceCards
      : evidenceTitle != null ||
          (evidenceStatements != null && evidenceStatements.length > 0)
        ? [
            {
              title: evidenceTitle,
              statements: evidenceStatements,
              cardBackGroundColor: evidenceCardBackGroundColor,
            },
          ]
        : [];

  const hasEvidence = resolvedEvidenceCards.length > 0;

  const hasTextError =
    !shouldHideTextBox &&
    isTextRequired &&
    (textareaValue == null || textareaValue.trim().length === 0);

  const handleChange = (optionValue: string, checked: boolean) => {
    const next = checked
      ? [...checkedValues, optionValue]
      : checkedValues.filter((v) => v !== optionValue);
    onCheckChange?.(optionValue, checked, next);
  };

  const cls = "criterion-detailed-checklist";

  const colorStyle = checkedColor
    ? ({ "--checklist-checked-color": checkedColor } as React.CSSProperties)
    : undefined;

  return (
    <section
      id={id}
      className={[cls, className].filter(Boolean).join(" ")}
      aria-labelledby={titleId}
      style={colorStyle}
    >
      {stepNumber != null && (
        <div className={`${cls}__step-column`}>
          <span className={`${cls}__step`}>{stepNumber}.</span>
        </div>
      )}

      <div className={`${cls}__body`}>
        <div className={`${cls}__header`}>
          <h3 id={titleId} className={`${cls}__title`}>
            {title}
          </h3>
          <div className={`${cls}__header-actions`}>
            {badge}
            {collapsible && (
              <button
                type="button"
                className={[
                  `${cls}__toggle`,
                  isExpanded ? "" : `${cls}__toggle--collapsed`,
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <ChevronDown size={16} />
              </button>
            )}
          </div>
        </div>

        <div
          className={[
            `${cls}__collapsible`,
            isExpanded
              ? `${cls}__collapsible--expanded`
              : `${cls}__collapsible--collapsed`,
          ].join(" ")}
          aria-hidden={!isExpanded}
        >
          <div className={`${cls}__collapsible-inner`}>
            {headerSlot != null && (
              <div className={`${cls}__header-slot`}>{headerSlot}</div>
            )}
            <div className={`${cls}__content`}>
              <div
                className={`${cls}__options`}
                role="group"
                aria-label={title}
              >
                {checklistOptions.map((option) => {
                  const isDisabled = checklistDisabled || option.disabled;
                  const isChecked = checkedValues.includes(option.value);
                  const optionId = `${checklistName}-${option.value}`;

                  return (
                    <label
                      key={option.value}
                      className={[
                        `${cls}__option`,
                        isDisabled ? `${cls}__option--disabled` : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      htmlFor={optionId}
                    >
                      <input
                        className={`${cls}__input`}
                        type="checkbox"
                        id={optionId}
                        name={checklistName}
                        value={option.value}
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={(e) =>
                          handleChange(option.value, e.target.checked)
                        }
                      />
                      <span className={`${cls}__box`}>
                        <svg
                          className={`${cls}__check-icon`}
                          viewBox="0 0 448 512"
                          fill="currentColor"
                        >
                          <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
                        </svg>
                      </span>
                      <span className={`${cls}__label`}>{option.label}</span>
                    </label>
                  );
                })}
              </div>

              <div
                className={[
                  `${cls}__textarea-reveal`,
                  shouldHideTextBox
                    ? `${cls}__textarea-reveal--hidden`
                    : `${cls}__textarea-reveal--visible`,
                ].join(" ")}
                aria-hidden={shouldHideTextBox}
              >
                <div className={`${cls}__textarea-section`}>
                  {textareaLabel && (
                    <label
                      id={textareaLabelId}
                      htmlFor={textareaId}
                      className={`${cls}__textarea-label`}
                    >
                      {textareaLabel}
                      {isTextRequired && (
                        <span
                          className={`${cls}__required-indicator`}
                          aria-hidden="true"
                        >
                          {" "}
                          *
                        </span>
                      )}
                    </label>
                  )}
                  <textarea
                    id={textareaId}
                    name={textareaName}
                    className={[
                      `${cls}__textarea`,
                      hasTextError ? `${cls}__textarea--error` : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    value={textareaValue}
                    placeholder={textareaPlaceholder}
                    disabled={textareaDisabled}
                    rows={textareaRows}
                    maxLength={textareaMaxLength}
                    required={isTextRequired}
                    aria-required={isTextRequired || undefined}
                    aria-invalid={hasTextError || undefined}
                    aria-labelledby={textareaLabel ? textareaLabelId : titleId}
                    onChange={(e) => onTextChange?.(e.target.value, e)}
                  />
                  {hasTextError && textRequiredErrorMessage && (
                    <span className={`${cls}__textarea-error`} role="alert">
                      {textRequiredErrorMessage}
                    </span>
                  )}
                </div>
              </div>

              {hasEvidence &&
                resolvedEvidenceCards.map((card, index) => (
                  <EvidenceCard
                    key={index}
                    title={card.title}
                    statements={card.statements}
                    cardBackGroundColor={card.cardBackGroundColor}
                    titleAction={card.titleAction}
                  />
                ))}
            </div>

            {children != null && (
              <div className={`${cls}__children`}>{children}</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { CriterionDetailedChecklist };
export default CriterionDetailedChecklist;
